import type { BadgeTone } from '@/components/ui/Badge';

import { DIARIZATION_ENABLED } from './pipelineFeatures';

export type StatusPresentation = {
  label: string;
  tone: BadgeTone;
  /** Обработка идёт — карточку/список стоит обновлять автоматически. */
  inProgress: boolean;
};

/**
 * Значения совпадают с `TranscriptionStatus` из `@axium/contracts`.
 */
const STATUS_MAP: Record<string, StatusPresentation> = {
  created: { label: 'Создано', tone: 'neutral', inProgress: false },
  uploading: { label: 'Загрузка', tone: 'info', inProgress: true },
  uploaded: { label: 'Загружено', tone: 'neutral', inProgress: true },
  preparing: { label: 'Подготовка', tone: 'info', inProgress: true },
  prepared: { label: 'Аудио подготовлено', tone: 'info', inProgress: true },
  transcribing: { label: 'Распознавание речи', tone: 'info', inProgress: true },
  transcribed: DIARIZATION_ENABLED
    ? { label: 'Речь распознана', tone: 'info', inProgress: true }
    : { label: 'Речь распознана', tone: 'success', inProgress: false },
  diarizing: DIARIZATION_ENABLED
    ? { label: 'Определение говорящих', tone: 'info', inProgress: true }
    : { label: 'Речь распознана', tone: 'success', inProgress: false },
  diarized: { label: 'Говорящие определены', tone: 'success', inProgress: false },
  summarizing: { label: 'Резюмирование', tone: 'info', inProgress: true },
  ready: { label: 'Готово', tone: 'success', inProgress: false },
  failed: { label: 'Ошибка', tone: 'danger', inProgress: false },
  canceled: { label: 'Отменено', tone: 'warning', inProgress: false },
};

/**
 * Терминальные статусы текущего конвейера.
 * Пока диаризация выключена, TRANSCRIBED — достаточный финал для UI.
 */
const PIPELINE_TERMINAL_STATUSES = new Set(
  DIARIZATION_ENABLED
    ? ['diarized', 'ready', 'failed', 'canceled']
    : ['transcribed', 'diarizing', 'diarized', 'ready', 'failed', 'canceled'],
);

/**
 * Неизвестный статус не подменяем: показываем как есть, чтобы интерфейс
 * не сообщал о состоянии, которого не было в ответе API.
 */
export function describeStatus(status: string | null): StatusPresentation {
  if (status === null || status.trim() === '') {
    return { label: 'Статус неизвестен', tone: 'neutral', inProgress: false };
  }
  return (
    STATUS_MAP[status.trim().toLowerCase()] ?? { label: status, tone: 'neutral', inProgress: false }
  );
}

/** @deprecated используйте isPipelineTerminal — имя оставлено для совместимости тестов. */
export function isSpeechTerminal(status: string | null): boolean {
  return isPipelineTerminal(status);
}

export function isPipelineTerminal(status: string | null): boolean {
  if (status === null || status.trim() === '') {
    return false;
  }
  return PIPELINE_TERMINAL_STATUSES.has(status.trim().toLowerCase());
}

/** Стоит ли карточке продолжать polling. */
export function shouldPollDetail(status: string | null): boolean {
  if (status === null || status.trim() === '') {
    return false;
  }
  const normalized = status.trim().toLowerCase();
  if (normalized === 'created') {
    return false;
  }
  return !PIPELINE_TERMINAL_STATUSES.has(normalized);
}

export type PrepStageId =
  | 'uploading'
  | 'uploaded'
  | 'preparing'
  | 'transcribing'
  | 'transcribed'
  | 'diarizing'
  | 'diarized';

export type PrepStage = {
  id: PrepStageId;
  label: string;
  /** Этап запланирован на будущее (серый, без таймера). */
  upcoming?: boolean;
};

/** @deprecated технические подэтапы; UI использует PIPELINE_VIEW_STAGES. */
export const PREP_STAGES: readonly PrepStage[] = [
  { id: 'uploading', label: 'Загрузка файла' },
  { id: 'uploaded', label: 'Проверка файла' },
  { id: 'preparing', label: 'Подготовка аудио' },
  { id: 'transcribing', label: 'Распознавание речи' },
  { id: 'transcribed', label: 'Речь распознана' },
  {
    id: 'diarizing',
    label: DIARIZATION_ENABLED ? 'Определение говорящих' : 'Определение говорящих · позже',
    upcoming: !DIARIZATION_ENABLED,
  },
  {
    id: 'diarized',
    label: DIARIZATION_ENABLED ? 'Говорящие определены' : 'Говорящие определены · позже',
    upcoming: !DIARIZATION_ENABLED,
  },
];

/** Три пользовательских этапа верхней панели. */
export type PipelineViewStageId = 'prepare' | 'speech' | 'speakers';

export type PipelineViewStageState = 'done' | 'current' | 'pending' | 'failed' | 'upcoming';

export type PipelineViewStage = {
  id: PipelineViewStageId;
  label: string;
  state: PipelineViewStageState;
  jobType: 'MEDIA_PROCESSING' | 'TRANSCRIPTION' | 'DIARIZATION';
};

export const PIPELINE_VIEW_STAGE_ORDER: readonly PipelineViewStageId[] = [
  'prepare',
  'speech',
  'speakers',
] as const;

const PIPELINE_VIEW_LABELS: Record<PipelineViewStageId, string> = {
  prepare: 'Подготовка файла',
  speech: 'Распознавание речи',
  speakers: 'Определение говорящих',
};

const PIPELINE_VIEW_JOB_TYPE: Record<
  PipelineViewStageId,
  'MEDIA_PROCESSING' | 'TRANSCRIPTION' | 'DIARIZATION'
> = {
  prepare: 'MEDIA_PROCESSING',
  speech: 'TRANSCRIPTION',
  speakers: 'DIARIZATION',
};

function normalizeStatus(status: string | null): string {
  return status?.trim().toLowerCase() ?? '';
}

function normalizeFailureStage(failureStage: string | null): string {
  return failureStage?.trim().toUpperCase() ?? '';
}

/**
 * Какой пользовательский этап сейчас активен (или завершён последним).
 * Технические статусы UPLOADING/UPLOADED/PREPARING/PREPARED → prepare;
 * TRANSCRIBING → speech; TRANSCRIBED/DIARIZING → speakers (если вкл.).
 */
export function activePipelineViewStage(
  status: string | null,
  failureStage: string | null = null,
): PipelineViewStageId | null {
  const normalized = normalizeStatus(status);
  if (normalized === '') {
    return null;
  }

  if (normalized === 'failed') {
    const failure = normalizeFailureStage(failureStage);
    if (failure === 'TRANSCRIPTION') {
      return 'speech';
    }
    if (failure === 'DIARIZATION') {
      return 'speakers';
    }
    return 'prepare';
  }

  if (
    normalized === 'uploading' ||
    normalized === 'uploaded' ||
    normalized === 'preparing' ||
    normalized === 'prepared'
  ) {
    return 'prepare';
  }
  if (normalized === 'transcribing') {
    return 'speech';
  }
  if (normalized === 'transcribed' || normalized === 'diarizing') {
    return DIARIZATION_ENABLED ? 'speakers' : 'speech';
  }
  if (normalized === 'diarized' || normalized === 'ready') {
    return 'speakers';
  }
  return null;
}

export function resolvePipelineViewStages(
  status: string | null,
  failureStage: string | null = null,
): PipelineViewStage[] | null {
  const normalized = normalizeStatus(status);
  if (normalized === '' || normalized === 'created' || normalized === 'canceled') {
    return null;
  }

  const active = activePipelineViewStage(status, failureStage);
  if (active === null) {
    return null;
  }

  const failed = normalized === 'failed';
  const activeIndex = PIPELINE_VIEW_STAGE_ORDER.indexOf(active);

  return PIPELINE_VIEW_STAGE_ORDER.map((id, index) => {
    if (id === 'speakers' && !DIARIZATION_ENABLED && !(normalized === 'diarized' || normalized === 'ready')) {
      return {
        id,
        label: PIPELINE_VIEW_LABELS[id],
        state: 'upcoming' as const,
        jobType: PIPELINE_VIEW_JOB_TYPE[id],
      };
    }

    let state: PipelineViewStageState;
    if (failed && index === activeIndex) {
      state = 'failed';
    } else if (index < activeIndex) {
      state = 'done';
    } else if (index === activeIndex) {
      const terminalDone =
        !failed &&
        ((id === 'speech' &&
          !DIARIZATION_ENABLED &&
          (normalized === 'transcribed' || normalized === 'diarizing')) ||
          (id === 'speakers' && (normalized === 'diarized' || normalized === 'ready')));
      const prepareComplete = id === 'prepare' && normalized === 'prepared';
      state = terminalDone || prepareComplete ? 'done' : failed ? 'failed' : 'current';
    } else {
      state = 'pending';
    }

    return {
      id,
      label: PIPELINE_VIEW_LABELS[id],
      state,
      jobType: PIPELINE_VIEW_JOB_TYPE[id],
    };
  });
}

const STATUS_TO_STAGE: Record<string, PrepStageId> = {
  uploading: 'uploading',
  uploaded: 'uploaded',
  preparing: 'preparing',
  prepared: 'transcribing',
  transcribing: 'transcribing',
  transcribed: DIARIZATION_ENABLED ? 'diarizing' : 'transcribed',
  diarizing: DIARIZATION_ENABLED ? 'diarizing' : 'transcribed',
  diarized: 'diarized',
  ready: 'diarized',
};

export function activePrepStage(status: string | null): PrepStageId | null {
  if (status === null || status.trim() === '') {
    return null;
  }
  return STATUS_TO_STAGE[status.trim().toLowerCase()] ?? null;
}

/** Тип ProcessingJob для этапа UI. */
export function jobTypeForPrepStage(
  stageId: PrepStageId,
): 'MEDIA_PROCESSING' | 'TRANSCRIPTION' | 'DIARIZATION' | null {
  if (stageId === 'uploading' || stageId === 'uploaded' || stageId === 'preparing') {
    return 'MEDIA_PROCESSING';
  }
  if (stageId === 'transcribing' || stageId === 'transcribed') {
    return 'TRANSCRIPTION';
  }
  if (stageId === 'diarizing' || stageId === 'diarized') {
    return 'DIARIZATION';
  }
  return null;
}

/** Job текущего статуса (для живого таймера в шапке прогресса). */
export function jobTypeForStatus(
  status: string | null,
): 'MEDIA_PROCESSING' | 'TRANSCRIPTION' | 'DIARIZATION' | null {
  const view = activePipelineViewStage(status);
  if (view === null) {
    return null;
  }
  return PIPELINE_VIEW_JOB_TYPE[view];
}

/** Плеер доступен, как только медиа подготовлено. */
export function isPlayerReadyStatus(status: string | null): boolean {
  if (status === null) {
    return false;
  }
  const normalized = status.trim().toLowerCase();
  return (
    normalized === 'prepared' ||
    normalized === 'transcribing' ||
    normalized === 'transcribed' ||
    normalized === 'diarizing' ||
    normalized === 'diarized' ||
    normalized === 'ready' ||
    normalized === 'failed'
  );
}

/**
 * Исходный текст Whisper доступен после TRANSCRIBED и далее
 * (включая DIARIZING / DIARIZED / ошибку диаризации).
 */
export function isTranscriptAvailableStatus(status: string | null): boolean {
  if (status === null) {
    return false;
  }
  const normalized = status.trim().toLowerCase();
  return (
    normalized === 'transcribed' ||
    normalized === 'diarizing' ||
    normalized === 'diarized' ||
    normalized === 'ready' ||
    normalized === 'failed'
  );
}

/** Пользовательские реплики по спикерам — только после DIARIZED. */
export function isDiarizationAvailableStatus(status: string | null): boolean {
  if (!DIARIZATION_ENABLED || status === null) {
    return false;
  }
  const normalized = status.trim().toLowerCase();
  return normalized === 'diarized' || normalized === 'ready';
}
